import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import TextInput from '__components/Inputs/TextInput';
import LongText from '__components/Inputs/LongText';
import Loader from '__components/Kizen/Loader';
import Switch from '__components/Kizen/Switch';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { knowledgeCatalogQuery } from 'query/knowledge';
import KnowledgeService from 'services/KnowledgeService';
import useCan from 'hooks/useCan';
import { SettingsContentCard } from '__components/Card/Card';
import { SubSectionWithHeader as SubSection } from 'components/Layout/SubSection';
import Cols from '__components/Layout/Cols';
import InputControl from '__components/Inputs/InputControl';
import { KizenTypography } from '__app/typography';
import { Icon } from '@kizen/kds/Icon';
import { useSetSaveAction } from '../ToolbarContext';
import { Typography } from '@kizen/kds/Typography';
import { getDefaultCatalogDescription } from 'pages/KnowledgeSettings/constants';
import {
  getPluginApiName,
  isPluginCatalog,
} from 'pages/KnowledgeSettings/helpers';
import { PluginContext } from 'ts-components/Plugins/context';

const StyledSettingsContentCard = styled(SettingsContentCard)`
  margin: 15px auto 20px;
`;

const StyledSourceSummary = styled(KizenTypography)`
  margin-top: 10px;
  color: var(--color-font-placeholder);
`;

const StyledSharedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  color: var(--color-font-placeholder);
`;

export const CatalogSettings = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const setSaveAction = useSetSaveAction();

  const canWriteSection = useCan({
    edit: 'sections',
    for: 'settings_section.knowledgebase_setting',
  });

  const queryOptions = useMemo(
    () =>
      knowledgeCatalogQuery(catalogId, {
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!catalogId,
      }),
    [catalogId]
  );

  const query = useQuery(queryOptions);
  const { data: catalog, isLoading } = query;

  const isDefaultCatalog = catalog?.is_default ?? false;
  const isPlugin = isPluginCatalog(catalog);
  const canWrite = canWriteSection;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [isPublishable, setIsPublishable] = useState(false);

  const prevQuery = useRef<typeof query | null>(null);
  if (catalog && prevQuery.current?.data?.id !== catalog.id) {
    setName(catalog.name);
    setDescription(
      catalog.description ??
        (catalog.is_default ? getDefaultCatalogDescription(t) : '')
    );
    setIsEnabled(catalog.is_enabled ?? true);
    setIsPublishable(catalog.is_publishable ?? false);
  }
  prevQuery.current = query;
  const { plugins } = useContext(PluginContext);
  const pluginApiName = getPluginApiName(catalog);
  const pluginDisplayName = plugins[pluginApiName]?.name || pluginApiName;

  const { mutate: saveCatalog, isPending: isSaving } = useMutation({
    mutationFn: () =>
      KnowledgeService.updateKnowledgeCatalog(catalogId, {
        name: isDefaultCatalog || isPlugin ? undefined : name.trim(),
        description: isPlugin ? undefined : description.trim() || undefined,
        is_enabled: isDefaultCatalog ? undefined : isEnabled,
        is_publishable: isPlugin ? undefined : isPublishable,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      showToast({
        message: t('Catalog saved successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error) => {
      if (catalog) {
        setName(catalog.name);
        setDescription(
          catalog.description ??
            (catalog.is_default ? getDefaultCatalogDescription(t) : '')
        );
        setIsEnabled(catalog.is_enabled ?? true);
        setIsPublishable(catalog.is_publishable ?? false);
      }
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to save catalog'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleSave = useCallback(() => {
    if (!name.trim() && !isDefaultCatalog && !isPlugin) {
      setNameError(t('Please name your catalog.'));
      return;
    }
    setNameError('');
    saveCatalog();
  }, [name, isDefaultCatalog, isPlugin, saveCatalog, t]);

  const nameChanged = !isDefaultCatalog && !isPlugin && name !== catalog?.name;
  const descriptionChanged =
    !isPlugin && description !== (catalog?.description ?? '');
  const enabledChanged =
    !isDefaultCatalog && isEnabled !== (catalog?.is_enabled ?? true);
  const publishableChanged =
    !isPlugin && isPublishable !== (catalog?.is_publishable ?? false);
  const isDirty =
    catalog &&
    (nameChanged || descriptionChanged || enabledChanged || publishableChanged);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!canWrite) {
      setSaveAction(null);
      return () => setSaveAction(null);
    }
    setSaveAction({
      onSave: () => handleSaveRef.current(),
      disabled: !isDirty || isSaving,
    });
    return () => setSaveAction(null);
  }, [canWrite, isDirty, isSaving, setSaveAction]);

  return (
    <Loader loading={isLoading}>
      <StyledSettingsContentCard>
        <SubSection title={t('Knowledge Catalog Settings')} action={null}>
          <Cols columns={2} gutter="20px">
            <InputControl margin>
              <TextInput
                label={t('Name')}
                value={name}
                onChange={setName}
                disabled={!canWrite || catalog?.is_default || isPlugin}
                error={nameError || undefined}
                data-qa-id="catalog-settings-name-input"
              />
            </InputControl>
            <InputControl margin>
              <TextInput
                label={t('API Name')}
                value={catalog?.api_name ?? ''}
                disabled
              />
            </InputControl>
            {!catalog?.is_default ? (
              <InputControl margin label={t('Enabled')}>
                <Switch
                  checked={isEnabled}
                  onChange={() => setIsEnabled((prev) => !prev)}
                  disabled={!canWrite}
                  removeMargin
                  dataQa="catalog-settings-enabled-toggle"
                />
              </InputControl>
            ) : null}
            <InputControl margin label={t('Publish to Marketplace')}>
              <Switch
                checked={isPublishable}
                onChange={() => setIsPublishable((prev) => !prev)}
                disabled={!canWrite || isPlugin}
                removeMargin
                dataQa="catalog-settings-publishable-toggle"
              />
            </InputControl>
          </Cols>
          <InputControl margin>
            <LongText
              label={t('Catalog Description')}
              placeholder={t('Enter Description')}
              value={description}
              onChange={setDescription}
              disabled={!canWrite || isPlugin}
              data-qa-id="catalog-settings-description-input"
            />
          </InputControl>
          {catalog ? (
            <StyledSourceSummary>
              {t(
                '{{fileCount}} file {{fileSources}}, {{onlineCount}} online {{onlineSources}}',
                {
                  fileCount: catalog.file_source_count ?? 0,
                  fileSources:
                    Number(catalog.file_source_count) === 1
                      ? t('source')
                      : t('sources'),
                  onlineCount: catalog.online_source_count ?? 0,
                  onlineSources:
                    Number(catalog.online_source_count) === 1
                      ? t('source')
                      : t('sources'),
                }
              )}
            </StyledSourceSummary>
          ) : null}
          {catalog?.is_default ? (
            <StyledSharedRow>
              <Icon
                icon="message-info-circle"
                size="md"
                color="font-placeholder"
              />
              <Typography size="md">
                {t('Shared with all team members')}
              </Typography>
            </StyledSharedRow>
          ) : null}
          {isPlugin ? (
            <StyledSharedRow>
              <Icon
                icon="custom-puzzle-piece"
                size="md"
                color="font-placeholder"
              />
              <Typography size="md" color="font-placeholder">
                {t('Installed from {{appName}} App', {
                  appName: pluginDisplayName,
                })}
              </Typography>
            </StyledSharedRow>
          ) : null}
        </SubSection>
      </StyledSettingsContentCard>
    </Loader>
  );
};
